/**
 * The pre-flight brief and the safety meeting (MB's R161; decisions 916, 917, 974; docs/specs/safety-brief.md; the
 * design draft docs/proposals/2026-11-03-preflight-brief.md). One shared record of the project with two kinds,
 * "Pre-flight brief" (each flying day, with a re-confirm for a second crew) and "Safety meeting" (SMS-02's
 * replacement); signs and actions are rows of their own, so several phones sign at once and an action carries from
 * one brief or meeting to the next. Made on Teslyn Mobile with no signal, read only in Teslyn Field and Teslyn
 * Cloud, printed by Teslyn Field (Typst).
 *
 * Everything the screens, the PDF and the database share lives here: who may do what, the suggested crew check list
 * and the project's own (`brief_setting`), the block pages (lines remaining with R136's rule, the boundary, the
 * hazards within 15 km numbered H1, H2 ...), the SVG scene each map is drawn from, the day's brief and the
 * re-confirm, the signs (own phone, leader's phone, before the last change), the actions, "since the last brief",
 * the PDF's name and manifest.
 */
import { blockFeatures } from './map-layers.js';
import { REMAINING_LEGEND, linesRemaining } from './lines-remaining.js';
import { arrowPathAt, arrowStations, planeArrows } from './direction-arrows.js';
import { formatKm } from './km.js';
import type { Block, DailyEntry, LineStatus, PlannedLine, Project, ProjectCrew, Person } from './model.js';
import { pinIcon, type HazardPinRecord } from './hazard-pins.js';
import type { PortalHazards } from './portal-hazards.js';
import type { ShapeLayerRecord } from './shape-files.js';
import type { KmlGeometry } from './map-kml.js';
import { planProgress, type LineState } from './progress.js';
import { plannedLineRole } from './reflights.js';
import { crewOnDate } from './crew-dates.js';
import { REGISTER_ROLE_LABEL } from './staff.js';
import { jspFileDate, jspPrintedDate } from './jsp-meeting.js';
import { briefWeatherBlocks, type BriefWeather, type BriefWeatherBlock } from './safety-brief-weather.js';

// ------------------------------------------------------------------------------------------------ kinds and roles

export type BriefKind = 'preflight' | 'safety_meeting';
export const BRIEF_KINDS: readonly BriefKind[] = ['preflight', 'safety_meeting'];

export const BRIEF_KIND_WORDS: Readonly<Record<BriefKind, string>> = {
  preflight: 'Pre-flight brief',
  safety_meeting: 'Safety meeting',
};

/** Lead, fill in and sign briefs and meetings (decision 917: pilots too). Office staff read only. */
export const BRIEF_WRITE_ROLES = [
  'tenant_admin',
  'manager',
  'field_crew',
  'data_processor',
  'pilot',
] as const;
/** Read the briefs list (Teslyn Field, Teslyn Cloud, the phone). */
export const BRIEF_READ_ROLES = [...BRIEF_WRITE_ROLES, 'office_staff'] as const;
/** The Project settings section "Pre-flight brief" (decision 974.5: managers and operators; tenant admins too). */
export const BRIEF_SETTING_ROLES = ['tenant_admin', 'manager', 'field_crew'] as const;

const any = (roles: readonly string[] | null | undefined, allowed: readonly string[]): boolean =>
  (roles ?? []).some((r) => allowed.includes(r));

export const canWriteBrief = (roles: readonly string[] | null | undefined): boolean =>
  any(roles, BRIEF_WRITE_ROLES);
export const canReadBriefs = (roles: readonly string[] | null | undefined): boolean =>
  any(roles, BRIEF_READ_ROLES);
export const canChangeBriefSetting = (roles: readonly string[] | null | undefined): boolean =>
  any(roles, BRIEF_SETTING_ROLES);

/** Hazards listed with a block: within this distance of its boundary (decision 974.7). */
export const BRIEF_HAZARD_KM = 15;

/** The limits the phone, the laptop's database and Teslyn Cloud all check. */
export const BRIEF_LIMITS = {
  location: 200,
  name: 200,
  attendees: 40,
  item: 1000,
  items: 100,
  notes: 4000,
  action: 500,
  checkText: 200,
  customChecks: 30,
  conditionsNote: 1000,
} as const;

// ------------------------------------------------------------------------------------------------ the crew check list

/** Who ticks a check: each pilot, each operator, or everyone present. */
export type CheckWho = 'pilot' | 'operator' | 'all';

export const CHECK_WHO_WORDS: Readonly<Record<CheckWho, string>> = {
  pilot: 'Each pilot',
  operator: 'Each operator',
  all: 'Everyone',
};

export interface CrewCheck {
  readonly id: string;
  readonly text: string;
  readonly who: CheckWho;
}

/**
 * The suggested crew checks (decision 974.5), ids fixed for good: the first six are the design draft's list (section
 * 4, item 7) and are on unless the project chooses otherwise; the rest are offered in Project settings.
 */
export const SUGGESTED_CREW_CHECKS: readonly (CrewCheck & { readonly on: boolean })[] = [
  {
    id: 'fit_to_fly',
    text: 'Fit to fly: rested, well, no medication or fatigue that affects flying',
    who: 'pilot',
    on: true,
  },
  { id: 'fuel', text: 'Fuel on board for today’s lines and the reserve', who: 'pilot', on: true },
  { id: 'mr_hours', text: 'Maintenance release hours left checked', who: 'pilot', on: true },
  { id: 'tracker_sos', text: 'Tracker on, SOS tested, check-in times agreed', who: 'operator', on: true },
  { id: 'hazards_briefed', text: 'Each hazard briefed with its height plan', who: 'all', on: true },
  { id: 'lines_agreed', text: 'Today’s lines and their order agreed', who: 'all', on: true },
  { id: 'landing_areas', text: 'Emergency landing areas briefed', who: 'pilot', on: false },
  { id: 'weather_minimums', text: 'Weather minimums and diversion agreed', who: 'all', on: false },
  { id: 'survival_kit', text: 'Survival kit, water and first aid on board', who: 'operator', on: false },
  { id: 'loose_items', text: 'Doors and loose items secured', who: 'all', on: false },
  { id: 'duty_hours', text: 'Duty and flight time within limits', who: 'pilot', on: false },
  { id: 'strip_animals', text: 'Birds and animals on or near the strip checked', who: 'pilot', on: false },
];

/** The project's choice (`brief_setting.checks`). */
export interface BriefSettingChecks {
  /** The suggested checks in use, by id. */
  readonly selected: readonly string[];
  /** The project's own items. */
  readonly custom: readonly CrewCheck[];
}

/** A `brief_setting` row: one per project, its id the project's id. */
export interface BriefSetting {
  readonly id: string;
  readonly project_id: string;
  readonly checks: BriefSettingChecks;
  readonly client_visible: boolean;
  readonly changed_by_name: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly deleted_at: string | null;
}

export const DEFAULT_BRIEF_SETTING_CHECKS: BriefSettingChecks = {
  selected: SUGGESTED_CREW_CHECKS.filter((c) => c.on).map((c) => c.id),
  custom: [],
};

/** The prefix of a project's own check id (`c-<uuid>`), so it never takes a suggested id. */
export const CUSTOM_CHECK_PREFIX = 'c-';

/** The check list a new brief asks: the suggested ones chosen, in the suggested order, then the project's own. */
export function briefChecks(
  setting: Pick<BriefSetting, 'checks' | 'deleted_at'> | null | undefined,
): CrewCheck[] {
  const c = setting && setting.deleted_at === null ? setting.checks : DEFAULT_BRIEF_SETTING_CHECKS;
  const chosen = new Set(c.selected);
  return [
    ...SUGGESTED_CREW_CHECKS.filter((s) => chosen.has(s.id)).map(({ id, text, who }) => ({ id, text, who })),
    ...c.custom.map(({ id, text, who }) => ({ id, text: text.trim(), who })),
  ];
}

/** What is wrong with a check list choice, in words (empty when it may be saved). */
export function checkBriefSettingChecks(c: BriefSettingChecks): string[] {
  const out: string[] = [];
  const known = new Set(SUGGESTED_CREW_CHECKS.map((s) => s.id));
  if (c.selected.some((id) => !known.has(id))) out.push('A chosen check is not on the suggested list.');
  if (c.custom.length > BRIEF_LIMITS.customChecks)
    out.push(`At most ${BRIEF_LIMITS.customChecks} items of the project’s own.`);
  const seen = new Set<string>();
  for (const x of c.custom) {
    const t = x.text.trim();
    if (!x.id.startsWith(CUSTOM_CHECK_PREFIX)) out.push('An added item has no id of its own.');
    if (t === '') out.push('An added item needs words.');
    if (t.length > BRIEF_LIMITS.checkText)
      out.push(`An item is at most ${BRIEF_LIMITS.checkText} characters.`);
    if (!(['pilot', 'operator', 'all'] as const).includes(x.who)) out.push('Say who ticks each item.');
    const k = t.toLowerCase();
    if (k && seen.has(k)) out.push(`“${t}” is on the list twice.`);
    seen.add(k);
  }
  if (c.selected.length + c.custom.length === 0) out.push('Keep at least one check on the list.');
  return [...new Set(out)];
}

/** Who ticks what: the register role on the brief's attendee row. */
export function checkWhoOfRole(role: string): CheckWho | null {
  const r = role.trim().toLowerCase();
  if (r === 'pilot') return 'pilot';
  if (r === 'operator' || r === 'field crew') return 'operator';
  return null;
}

/** The checks a person ticks: their role's and everyone's. */
export function checksFor(checks: readonly CrewCheck[], role: string): CrewCheck[] {
  const who = checkWhoOfRole(role);
  return checks.filter((c) => c.who === 'all' || c.who === who);
}

// ------------------------------------------------------------------------------------------------ the records

export interface BriefAttendee {
  readonly person_id: string | null;
  readonly name: string;
  /** "Pilot", "Operator", "Manager" ... */
  readonly role: string;
  /** A relief pilot added on site who is not on the crew list (decision 471). */
  readonly relief: boolean;
}

export type BriefItemKind = 'item' | 'incident' | 'near_miss';
export const BRIEF_ITEM_WORDS: Readonly<Record<BriefItemKind, string>> = {
  item: 'New item',
  incident: 'Incident',
  near_miss: 'Near miss',
};

export interface BriefItem {
  readonly id: string;
  readonly kind: BriefItemKind;
  readonly text: string;
}

export type BriefHazardSource = 'pin' | 'pack' | 'shape';

/** A hazard as the brief lists it, with its H number and the "Briefed" tick. */
export interface BriefHazard {
  /** "H1". */
  readonly n: string;
  /** The hazard's own reference (a pin's id, a pack placemark's folder and number). */
  readonly ref: string;
  readonly source: BriefHazardSource;
  /** The pin icon key (towers and powerlines of the pack: 'tower', 'powerline'), or ''. */
  readonly icon: string;
  readonly name: string;
  /** What it is, in words: the pin's description, the pack's folder and description. */
  readonly detail: string;
  /** The blocks it is listed with, by name. */
  readonly blocks: readonly string[];
  /** Its distance from each of those blocks' boundaries, km (0 inside), in the order of `blocks`. */
  readonly block_km?: readonly number[];
  /** The nearest distance to any of those blocks' boundaries, km, 0 when inside. */
  readonly distance_km: number;
  readonly inside: boolean;
  readonly briefed: boolean;
}

export interface BriefAircraft {
  readonly id: string;
  readonly registration: string;
  /** Words about it: "longest flight 6.5 h", "MR: 42.3 h left". */
  readonly notes: readonly string[];
}

/** One block page's figures, kept in the snapshot. */
export interface BriefBlockSummary {
  readonly block_id: string;
  readonly name: string;
  readonly text: string;
  readonly remaining_km: number;
  readonly planned_km: number;
  /** "97 Unflown", "2 Reflight planned". */
  readonly counts: readonly string[];
  /** "190051 reflight, 26.44 km", "102731 patch, 4.40 km". */
  readonly open: readonly string[];
  /** "Traverse 100 m at 90°", "Terrain clearance 60 m". */
  readonly facts: readonly string[];
}

export interface BriefSnapshot {
  /** When the layers the pages come from were read from Teslyn Cloud (ISO), or ''. */
  readonly as_at: string;
  readonly aircraft: readonly BriefAircraft[];
  readonly blocks: readonly BriefBlockSummary[];
  /** "Milgun Patch: nothing remains". */
  readonly finished: readonly string[];
  /** One line about the hazards further away, or ''. */
  readonly far_hazards: string;
  readonly since_last: readonly string[];
  /** The latest JSP meeting's risks and mitigations, in words. */
  readonly standing: readonly string[];
  /**
   * Each block's map as the crew saw it (`briefBlockScene`), by block id, so the PDF draws that morning's lines and
   * hazards rather than the laptop's later state. Absent on a brief whose phone did not keep them.
   */
  readonly scenes?: Readonly<Record<string, BriefScene>>;
  /**
   * The NAIPS location briefing of the project's aerodromes the crew saw (decision 989): TAF, METAR and NOTAMs as
   * NAIPS printed them, with when Teslyn Cloud had it. Absent or null when there was none on the phone.
   */
  readonly weather?: BriefWeather | null;
}

export const EMPTY_BRIEF_SNAPSHOT: BriefSnapshot = {
  as_at: '',
  aircraft: [],
  blocks: [],
  finished: [],
  far_hazards: '',
  since_last: [],
  standing: [],
};

export interface BriefConditions {
  readonly reviewed: boolean;
  readonly note: string;
}

/** A `safety_brief` row as the phone and the laptop hold it. */
export interface SafetyBrief {
  readonly id: string;
  readonly project_id: string;
  readonly kind: BriefKind;
  readonly brief_date: string;
  readonly opened_at: string;
  readonly location: string;
  readonly leader_person_id: string | null;
  readonly leader_name: string;
  readonly attendees: readonly BriefAttendee[];
  readonly snapshot: BriefSnapshot;
  readonly hazards: readonly BriefHazard[];
  readonly checks: readonly CrewCheck[];
  readonly conditions: BriefConditions;
  readonly items: readonly BriefItem[];
  readonly notes: string;
  readonly next_meeting: string | null;
  readonly finished_at: string | null;
  readonly content_changed_at: string;
  readonly pdf_name: string;
  readonly pdf_sha256: string;
  readonly pdf_revision: number;
  readonly pdf_made_at: string | null;
  readonly recorded_by_name: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly updated_by?: string | null;
  readonly deleted_at: string | null;
  /** Cloud only: the sign-in that started it. */
  readonly recorded_by?: string | null;
  readonly tenant_id?: string;
}

/** A `safety_brief_sign` row: one person's "Read and understood", never changed after. */
export interface SafetyBriefSign {
  readonly id: string;
  readonly project_id: string;
  readonly brief_id: string;
  readonly person_id: string | null;
  readonly name: string;
  readonly role: string;
  readonly checks: readonly string[];
  readonly reconfirm: boolean;
  readonly on_leader_phone: boolean;
  readonly signed_at: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly updated_by?: string | null;
  readonly deleted_at: string | null;
  /** Cloud only: the sign-in that made the row. */
  readonly signed_by?: string | null;
  readonly tenant_id?: string;
}

/** A `safety_action` row, shared by every brief and meeting of the project. */
export interface SafetyAction {
  readonly id: string;
  readonly project_id: string;
  readonly brief_id: string;
  readonly text: string;
  readonly owner_person_id: string | null;
  readonly owner_name: string;
  readonly all_crew: boolean;
  readonly due_date: string | null;
  readonly done_at: string | null;
  readonly done_brief_id: string | null;
  readonly done_by_name: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly updated_by?: string | null;
  readonly deleted_at: string | null;
  readonly tenant_id?: string;
}

// ------------------------------------------------------------------------------------------------ hazards and distance

type Pt = readonly [number, number];

/** A hazard the brief may list: a hazard pin, a placemark of the pilot hazard pack or a shape file hazard. */
export interface BriefHazardInput {
  readonly ref: string;
  readonly source: BriefHazardSource;
  readonly icon: string;
  readonly name: string;
  readonly detail: string;
  readonly shape:
    | { readonly type: 'point'; readonly at: Pt }
    | { readonly type: 'line'; readonly points: readonly Pt[] }
    | { readonly type: 'polygon'; readonly points: readonly Pt[] };
}

/** The project's hazard pins (decision 620), live only. */
export function pinHazardInputs(pins: readonly HazardPinRecord[]): BriefHazardInput[] {
  return pins
    .filter((p) => p.deleted_at === null)
    .map((p) => ({
      ref: p.id,
      source: 'pin' as const,
      icon: pinIcon(p.icon).key,
      name: p.name.trim() || 'Hazard pin',
      detail: [p.description.trim(), p.placed_by_name ? `placed by ${p.placed_by_name}` : '']
        .filter(Boolean)
        .join(' · '),
      shape: { type: 'point' as const, at: [p.lat, p.lng] as const },
    }));
}

const iconOfFolder = (folder: string, kind: 'point' | 'line' | 'polygon'): string => {
  const f = folder.toLowerCase();
  if (/power|transmission|\blv\b|\bhv\b/.test(f)) return 'powerline';
  if (/tower|mast|repeater|communication/.test(f)) return 'tower';
  return kind === 'point' ? 'tower' : '';
};

/** The hazards of the pilot hazard pack as the client portal reads them (landholder properties never). */
export function packHazardInputs(pack: PortalHazards | null | undefined): BriefHazardInput[] {
  const out: BriefHazardInput[] = [];
  for (const layer of pack?.layers ?? []) {
    // the pack's own boundary folders (`BND: <file>`, Teslyn Field's build_pack) are the survey area, not hazards
    if (layer.kind !== 'hazards' || /^BND:/i.test(layer.name.trim())) continue;
    layer.placemarks.forEach((m, i) => {
      const folder = [layer.name, m.folder].filter((x) => x.trim()).join(' › ');
      const ref = `${layer.name}:${i}`;
      const detail = [folder, m.description.trim()].filter(Boolean).join(' · ');
      const name = m.name.trim() || layer.name;
      if (m.shape.type === 'point')
        out.push({
          ref,
          source: 'pack',
          icon: iconOfFolder(folder, 'point'),
          name,
          detail,
          shape: { type: 'point', at: [m.shape.at.lat, m.shape.at.lng] },
        });
      else if (m.shape.type === 'line')
        out.push({
          ref,
          source: 'pack',
          icon: iconOfFolder(folder, 'line'),
          name,
          detail,
          shape: { type: 'line', points: m.shape.points.map((p) => [p.lat, p.lng] as const) },
        });
      else
        out.push({
          ref,
          source: 'pack',
          icon: iconOfFolder(folder, 'polygon'),
          name,
          detail,
          shape: { type: 'polygon', points: m.shape.outer.map((p) => [p.lat, p.lng] as const) },
        });
    });
  }
  return out;
}

/** The shape file hazards of the project (decision 921: a shape file's hazards are hazards). */
export function shapeHazardInputs(layers: readonly ShapeLayerRecord[]): BriefHazardInput[] {
  const out: BriefHazardInput[] = [];
  const add = (layer: ShapeLayerRecord, name: string, g: KmlGeometry, ref: string): void => {
    if (g.type === 'multi') {
      g.parts.forEach((p, j) => add(layer, name, p, `${ref}.${j}`));
      return;
    }
    const base = { ref, source: 'shape' as const, name, detail: layer.name };
    if (g.type === 'point')
      out.push({ ...base, icon: 'tower', shape: { type: 'point', at: [g.at.lat, g.at.lng] } });
    else if (g.type === 'line_string')
      out.push({
        ...base,
        icon: iconOfFolder(layer.name, 'line'),
        shape: { type: 'line', points: g.points.map((p) => [p.lat, p.lng] as const) },
      });
    else
      out.push({
        ...base,
        icon: '',
        shape: { type: 'polygon', points: g.outer.map((p) => [p.lat, p.lng] as const) },
      });
  };
  for (const layer of layers) {
    if (layer.kind !== 'hazard' || (layer.deleted_at ?? null) !== null) continue;
    layer.features.forEach((f, i) => add(layer, f.name.trim() || layer.name, f.geometry, `${layer.id}:${i}`));
  }
  return out;
}

/** km east and north of a centre: flat enough within a few tens of kilometres. */
interface Flat {
  readonly lat0: number;
  readonly lng0: number;
  readonly kx: number;
  readonly ky: number;
}
const flatAround = (ring: readonly Pt[]): Flat => {
  const lat0 = ring.reduce((s, p) => s + p[0], 0) / Math.max(1, ring.length);
  const lng0 = ring.reduce((s, p) => s + p[1], 0) / Math.max(1, ring.length);
  return { lat0, lng0, kx: 111.32 * Math.cos((lat0 * Math.PI) / 180), ky: 110.574 };
};
const xy = (f: Flat, p: Pt): [number, number] => [(p[1] - f.lng0) * f.kx, (p[0] - f.lat0) * f.ky];

function insideRing(p: [number, number], ring: readonly [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function segDist(p: [number, number], a: [number, number], b: [number, number]): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const l2 = dx * dx + dy * dy;
  const t = l2 === 0 ? 0 : Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2));
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

function segmentsCross(
  a: [number, number],
  b: [number, number],
  c: [number, number],
  d: [number, number],
): boolean {
  const o = (p: [number, number], q: [number, number], r: [number, number]): number =>
    Math.sign((q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]));
  return o(a, b, c) !== o(a, b, d) && o(c, d, a) !== o(c, d, b);
}

type Seg = [[number, number], [number, number]];
const edges = (pts: readonly [number, number][], closed: boolean): Seg[] => {
  const out: Seg[] = [];
  for (let i = 0; i + 1 < pts.length; i++) out.push([pts[i]!, pts[i + 1]!]);
  if (closed && pts.length > 2) out.push([pts[pts.length - 1]!, pts[0]!]);
  return out;
};

/**
 * The distance of a hazard from a boundary ring (lat, lng), in km; 0 when it is inside or crosses it. A point is
 * measured to the nearest edge, a line or an area to its nearest part.
 */
export function hazardDistanceKm(shape: BriefHazardInput['shape'], ring: readonly Pt[]): number {
  if (ring.length < 3) return Number.POSITIVE_INFINITY;
  const f = flatAround(ring);
  const r = ring.map((p) => xy(f, p));
  const rEdges = edges(r, true);
  const pts = (shape.type === 'point' ? [shape.at] : shape.points).map((p) => xy(f, p));
  if (pts.length === 0) return Number.POSITIVE_INFINITY;
  if (pts.some((p) => insideRing(p, r))) return 0;
  // the boundary inside an area hazard
  if (shape.type === 'polygon' && pts.length > 2 && insideRing(r[0]!, pts)) return 0;
  const hEdges = edges(pts, shape.type === 'polygon');
  for (const [a, b] of hEdges) for (const [c, d] of rEdges) if (segmentsCross(a, b, c, d)) return 0;
  let best = Number.POSITIVE_INFINITY;
  for (const p of pts) for (const [a, b] of rEdges) best = Math.min(best, segDist(p, a, b));
  for (const p of r) for (const [a, b] of hEdges) best = Math.min(best, segDist(p, a, b));
  return best;
}

/**
 * "inside the block", "1.2 km outside": from the named block when given (its own distance), else from the nearest of
 * the blocks it is listed with.
 */
export function hazardWhereWords(
  h: Pick<BriefHazard, 'inside' | 'distance_km'> & Partial<Pick<BriefHazard, 'blocks' | 'block_km'>>,
  block?: string,
): string {
  const at = block !== undefined && h.blocks && h.block_km ? h.blocks.indexOf(block) : -1;
  const km = at >= 0 ? h.block_km![at]! : h.distance_km;
  return km === 0 ? 'inside the block' : `${km.toFixed(1)} km outside`;
}

// ------------------------------------------------------------------------------------------------ the block pages

/** A line of a block's map, in the lines-remaining map's colour token and stroke. */
export interface BriefMapLine {
  readonly points: readonly Pt[];
  readonly colour: string;
  readonly stroke: 'thin' | 'normal' | 'thick';
  readonly dashed: boolean;
  readonly label: string;
  /** A patch or reflight still to fly: its planned direction (the Refly list's), drawn as arrows (decision 1011). */
  readonly direction_deg?: number;
}

/** One page of the brief: a block that still has lines to fly. */
export interface BriefBlockPage {
  readonly block_id: string;
  readonly name: string;
  readonly digit: string;
  /** The boundary (lat, lng), or null when none is recorded or it cannot be drawn. */
  readonly boundary: readonly Pt[] | null;
  readonly lines: readonly BriefMapLine[];
  readonly summary: BriefBlockSummary;
  /** The hazards listed on this page, by `ref`, nearest first. */
  readonly hazard_refs: readonly string[];
}

/** The block pages of a project and its hazards, as the phone keeps them for use with no signal. */
export interface BriefBlocks {
  readonly pages: readonly BriefBlockPage[];
  /** "Milgun Patch: nothing remains", one line per block with nothing left (decision 974.4). */
  readonly finished: readonly string[];
  /** Every hazard within 15 km of a block with lines left, numbered H1, H2 ... in page order. */
  readonly hazards: readonly BriefHazard[];
  /** The hazards' shapes by ref, for the maps. */
  readonly shapes: Readonly<Record<string, BriefHazardInput['shape']>>;
  /** The hazards further away, in one line, or ''. */
  readonly far: string;
  /** What could not be drawn ("Block Milgun: no boundary recorded"). */
  readonly notDrawn: readonly string[];
}

const STATE_ORDER: readonly LineState[] = [
  'not_flown',
  'awaiting_review',
  'reflight_planned',
  'patch_planned',
  'rejected',
  'partial',
];

const km2 = (m: number): string => `${formatKm(m / 1000)} km`;

/** One line per block with nothing left to fly, and the pages of the others. */
export function briefBlocks(input: {
  readonly project: Pick<Project, 'epsg'>;
  readonly blocks: readonly Block[];
  readonly plan: readonly PlannedLine[];
  readonly statuses: readonly LineStatus[];
  readonly hazards: readonly BriefHazardInput[];
  readonly withinKm?: number;
}): BriefBlocks {
  const within = input.withinKm ?? BRIEF_HAZARD_KM;
  const live = <T extends { deleted_at: string | null }>(xs: readonly T[]): T[] =>
    xs.filter((x) => x.deleted_at === null);
  const blocks = live(input.blocks)
    .filter((b) => b.kind === 'survey')
    .sort((a, b) => a.digit.localeCompare(b.digit) || a.name.localeCompare(b.name));
  const plan = live(input.plan);
  const statuses = live(input.statuses);
  const pages: BriefBlockPage[] = [];
  const finished: string[] = [];
  const notDrawn: string[] = [];
  const near = new Map<string, { blocks: string[]; km: number[]; distance: number }>();
  const pageDist = new Map<string, Map<string, number>>();
  for (const b of blocks) {
    const bPlan = plan.filter((l) => l.block_id === b.id);
    const ids = new Set(bPlan.map((l) => l.id));
    const bStatus = statuses.filter((s) => ids.has(s.planned_line_id));
    const progress = planProgress(bStatus, bPlan);
    const remaining = linesRemaining(bStatus, bPlan, [b], input.project);
    const left = progress.lines.filter((l) => l.state !== 'accepted');
    if (left.length === 0 || b.km_completed === true) {
      finished.push(`${b.name}: nothing remains`);
      continue;
    }
    const outline = blockFeatures([b], input.project);
    notDrawn.push(...outline.notDrawn, ...remaining.notDrawn.map((w) => `Block ${b.name}: ${w}`));
    const poly = outline.features[0]?.geometry;
    const boundary =
      poly && poly.type === 'polygon' && poly.rings[0]
        ? poly.rings[0].map((p) => [p.lat, p.lng] as const)
        : null;
    const lines: BriefMapLine[] = remaining.features.flatMap((f) =>
      f.geometry.type === 'line' && typeof f.style.colour === 'string'
        ? [
            {
              points: f.geometry.points.map((p) => [p.lat, p.lng] as const),
              colour: f.style.colour,
              stroke: f.style.stroke,
              dashed: f.style.dashed,
              label: f.label ?? '',
              ...(f.direction_deg !== undefined ? { direction_deg: f.direction_deg } : {}),
            },
          ]
        : [],
    );
    const counts = new Map<LineState, number>();
    const open: string[] = [];
    for (const lp of left) {
      counts.set(lp.state, (counts.get(lp.state) ?? 0) + 1);
      if (lp.open && plannedLineRole(lp.open) === 'patch')
        open.push(`${lp.open.number} patch, ${km2(lp.open.length_m)}`);
      else if (lp.state === 'reflight_planned' && lp.open)
        open.push(`${lp.open.number} reflight, ${km2(lp.original.length_m)}`);
    }
    const facts: string[] = [];
    if (b.trav_spacing_m)
      facts.push(
        `Traverse lines ${b.trav_spacing_m} m apart${b.trav_direction_deg != null ? ` at ${b.trav_direction_deg}°` : ''}`,
      );
    if (b.tie_spacing_m)
      facts.push(
        `Tie lines ${b.tie_spacing_m} m apart${b.tie_direction_deg != null ? ` at ${b.tie_direction_deg}°` : ''}`,
      );
    if (b.terrain_clearance_m) facts.push(`Terrain clearance ${b.terrain_clearance_m} m`);
    const dists = new Map<string, number>();
    if (boundary) {
      for (const h of input.hazards) {
        const d = hazardDistanceKm(h.shape, boundary);
        if (d <= within) {
          dists.set(h.ref, d);
          const n = near.get(h.ref) ?? { blocks: [], km: [], distance: Number.POSITIVE_INFINITY };
          n.blocks.push(b.name);
          n.km.push(Math.round(d * 10) / 10);
          n.distance = Math.min(n.distance, d);
          near.set(h.ref, n);
        }
      }
    }
    pageDist.set(b.id, dists);
    pages.push({
      block_id: b.id,
      name: b.name,
      digit: b.digit,
      boundary,
      lines,
      summary: {
        block_id: b.id,
        name: b.name,
        text: remaining.text,
        remaining_km: remaining.remainingM / 1000,
        planned_km: remaining.plannedM / 1000,
        counts: STATE_ORDER.filter((s) => (counts.get(s) ?? 0) > 0).map(
          (s) => `${counts.get(s)} ${REMAINING_LEGEND[s].word}`,
        ),
        open,
        facts,
      },
      hazard_refs: [...dists.entries()].sort((a, b) => a[1] - b[1]).map(([r]) => r),
    });
  }
  // H numbers in page order, nearest first within a page; a hazard near two blocks keeps its first number
  const byRef = new Map(input.hazards.map((h) => [h.ref, h]));
  const hazards: BriefHazard[] = [];
  const numbered = new Set<string>();
  for (const p of pages)
    for (const ref of p.hazard_refs) {
      if (numbered.has(ref)) continue;
      numbered.add(ref);
      const h = byRef.get(ref)!;
      const n = near.get(ref)!;
      hazards.push({
        n: `H${hazards.length + 1}`,
        ref,
        source: h.source,
        icon: h.icon,
        name: h.name,
        detail: h.detail,
        blocks: n.blocks,
        block_km: n.km,
        distance_km: Math.round(n.distance * 10) / 10,
        inside: n.distance === 0,
        briefed: false,
      });
    }
  const shapes: Record<string, BriefHazardInput['shape']> = {};
  for (const h of hazards) shapes[h.ref] = byRef.get(h.ref)!.shape;
  const farOnes = input.hazards.filter((h) => !numbered.has(h.ref));
  return { pages, finished, hazards, shapes, far: farHazardsWords(farOnes, within), notDrawn };
}

/** "Also in the hazards, more than 15 km from every block with lines left: 3 (Fortnum 1, Fortnum 2, Fortnum 3)." */
export function farHazardsWords(
  far: readonly Pick<BriefHazardInput, 'name'>[],
  withinKm = BRIEF_HAZARD_KM,
): string {
  if (far.length === 0) return '';
  const names = [...new Set(far.map((h) => h.name))];
  const shown = names.slice(0, 6).join(', ');
  return `Also in the hazards, more than ${withinKm} km from every block with lines left: ${far.length} (${shown}${names.length > 6 ? ', …' : ''}).`;
}

// ------------------------------------------------------------------------------------------------ the map scene (SVG)

export interface BriefScenePath {
  readonly d: string;
  /** A token name (`map-line-boundary`, `map-line-unflown` ...). */
  readonly colour: string;
  readonly width: number;
  readonly dashed: boolean;
  readonly fill: boolean;
}

export interface BriefSceneMarker {
  readonly x: number;
  readonly y: number;
  /** "H1". */
  readonly label: string;
  readonly inside: boolean;
}

/** A direction arrow on a patch or reflight of the scene (decision 1011): a closed path in the line's colour. */
export interface BriefSceneArrow {
  readonly d: string;
  /** The line's token name (`map-line-to-fly`, `map-line-reflight`). */
  readonly colour: string;
}

/** An arrow of the brief's map, scene units long (the scene is 600 wide, drawn at the phone's width). */
export const BRIEF_ARROW_SIZE = 18;

export interface BriefScene {
  readonly width: number;
  readonly height: number;
  readonly paths: readonly BriefScenePath[];
  readonly markers: readonly BriefSceneMarker[];
  /**
   * The direction arrows of the patches and reflights still to fly, over the lines (decision 1011). Absent on a scene
   * kept before they were drawn.
   */
  readonly arrows?: readonly BriefSceneArrow[];
  /** "North up · 5 km" for the scale bar. */
  readonly scaleKm: number;
  readonly scaleLength: number;
}

const STROKE_WIDTH: Readonly<Record<BriefMapLine['stroke'], number>> = { thin: 1, normal: 1.6, thick: 3.2 };

/**
 * The block's map as an SVG scene: north up, the boundary and the lines remaining, the hazards listed with it (lines
 * and areas drawn, points as numbered markers), framed on the boundary and those hazards. Drawn on the phone
 * (react-native-svg) and in the PDF (`briefBlockSvg`), so it needs no map tiles at all.
 */
export function briefBlockScene(
  page: BriefBlockPage,
  hazards: readonly BriefHazard[],
  shapes: Readonly<Record<string, BriefHazardInput['shape']>>,
  width = 600,
  maxHeight = 600,
): BriefScene {
  const listed = hazards.filter((h) => page.hazard_refs.includes(h.ref));
  const all: Pt[] = [
    ...(page.boundary ?? []),
    ...page.lines.flatMap((l) => l.points),
    ...listed.flatMap((h) => {
      const s = shapes[h.ref];
      return s ? (s.type === 'point' ? [s.at] : s.points) : [];
    }),
  ];
  if (all.length === 0) return { width, height: 1, paths: [], markers: [], scaleKm: 0, scaleLength: 0 };
  const f = flatAround(all);
  const ps = all.map((p) => xy(f, p));
  const minX = Math.min(...ps.map((p) => p[0]));
  const maxX = Math.max(...ps.map((p) => p[0]));
  const minY = Math.min(...ps.map((p) => p[1]));
  const maxY = Math.max(...ps.map((p) => p[1]));
  const pad = 16;
  const spanX = Math.max(0.5, maxX - minX);
  const spanY = Math.max(0.5, maxY - minY);
  const scale = Math.min((width - 2 * pad) / spanX, (maxHeight - 2 * pad) / spanY);
  const height = Math.round(spanY * scale + 2 * pad);
  const at = (p: Pt): [number, number] => {
    const [x, y] = xy(f, p);
    return [pad + (x - minX) * scale, height - pad - (y - minY) * scale];
  };
  const d = (pts: readonly Pt[], closed: boolean): string =>
    pts
      .map((p, i) => {
        const [x, y] = at(p);
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(' ') + (closed ? ' Z' : '');
  const paths: BriefScenePath[] = [];
  for (const h of listed) {
    const s = shapes[h.ref];
    if (!s || s.type === 'point') continue;
    paths.push({
      d: d(s.points, s.type === 'polygon'),
      colour: 'fail',
      width: 1.6,
      dashed: false,
      fill: s.type === 'polygon',
    });
  }
  if (page.boundary)
    paths.push({
      d: d(page.boundary, true),
      colour: 'map-line-boundary',
      width: 2,
      dashed: false,
      fill: false,
    });
  for (const l of page.lines)
    paths.push({
      d: d(l.points, false),
      colour: l.colour,
      width: STROKE_WIDTH[l.stroke],
      dashed: l.dashed,
      fill: false,
    });
  // the way each patch and reflight still to fly is planned to be flown, spaced for this scale (decision 1011)
  const metresPerPx = 1000 / scale;
  const arrows: BriefSceneArrow[] = page.lines.flatMap((l) => {
    if (l.direction_deg === undefined) return [];
    // the scene's y runs down: the plane's runs up
    const pts = l.points.map((p) => {
      const [x, y] = at(p);
      return [x, -y] as const;
    });
    const lengthPx = pts
      .slice(1)
      .reduce((m, p, i) => m + Math.hypot(p[0] - pts[i]![0], p[1] - pts[i]![1]), 0);
    return planeArrows(pts, arrowStations(lengthPx * metresPerPx, { metresPerPx })).map((a) => ({
      d: arrowPathAt(a.x, -a.y, a.rotation_deg, BRIEF_ARROW_SIZE),
      colour: l.colour,
    }));
  });
  const markers: BriefSceneMarker[] = listed.flatMap((h) => {
    const s = shapes[h.ref];
    if (!s) return [];
    const p = s.type === 'point' ? s.at : s.points[Math.floor(s.points.length / 2)]!;
    const [x, y] = at(p);
    return [{ x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10, label: h.n, inside: h.inside }];
  });
  // a round scale bar about a fifth of the width
  const kmWide = (width - 2 * pad) / scale;
  const steps = [0.5, 1, 2, 5, 10, 20, 50];
  const scaleKm = steps.reduce((b, s) => (s <= kmWide / 4 ? s : b), steps[0]!);
  return {
    width,
    height,
    paths,
    markers,
    ...(arrows.length > 0 ? { arrows } : {}),
    scaleKm,
    scaleLength: Math.round(scaleKm * scale),
  };
}

/** The scene as an SVG document, colours resolved by `colour` (token name to `#rrggbb`). */
export function briefBlockSvg(scene: BriefScene, colour: (token: string) => string): string {
  const esc = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;');
  const parts: string[] = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${scene.width}" height="${scene.height}" viewBox="0 0 ${scene.width} ${scene.height}">`,
    `<rect width="100%" height="100%" fill="${colour('surface-000')}"/>`,
  ];
  for (const p of scene.paths)
    parts.push(
      `<path d="${p.d}" fill="${p.fill ? colour(p.colour) : 'none'}" fill-opacity="${p.fill ? 0.15 : 0}" stroke="${colour(p.colour)}" stroke-width="${p.width}"${p.dashed ? ' stroke-dasharray="4 3"' : ''} stroke-linejoin="round" stroke-linecap="round"/>`,
    );
  // the direction arrows over the lines, edged dark as the lines are (decision 1011)
  for (const a of scene.arrows ?? [])
    parts.push(
      `<path d="${a.d}" fill="${colour(a.colour)}" stroke="${colour('map-line-casing')}" stroke-width="1.2" stroke-linejoin="round"/>`,
    );
  for (const m of scene.markers)
    parts.push(
      `<circle cx="${m.x}" cy="${m.y}" r="7" fill="${colour('fail')}" stroke="${colour('surface-000')}" stroke-width="1.5"/>`,
      `<text x="${m.x + 9}" y="${m.y + 4}" font-family="Archivo" font-size="11" font-weight="600" fill="${colour('ink')}">${esc(m.label)}</text>`,
    );
  if (scene.scaleLength > 0) {
    const y = scene.height - 8;
    parts.push(
      `<path d="M10 ${y} L${10 + scene.scaleLength} ${y}" stroke="${colour('ink')}" stroke-width="2"/>`,
      `<text x="10" y="${y - 4}" font-family="IBM Plex Mono" font-size="10" fill="${colour('ink')}">${scene.scaleKm} km · north up</text>`,
    );
  }
  parts.push('</svg>');
  return parts.join('');
}

// ------------------------------------------------------------------------------------------------ the day's brief

const live = <T extends { deleted_at: string | null }>(xs: readonly T[]): T[] =>
  xs.filter((x) => x.deleted_at === null);

/** The day's pre-flight brief of the project (decision 974.1: one per flying day), the first started if two were. */
export function briefOfDay(briefs: readonly SafetyBrief[], date: string): SafetyBrief | null {
  return (
    live(briefs)
      .filter((b) => b.kind === 'preflight' && b.brief_date === date)
      .sort((a, b) => a.opened_at.localeCompare(b.opened_at) || a.id.localeCompare(b.id))[0] ?? null
  );
}

/** Briefs and meetings newest first. */
export function briefsNewestFirst<B extends SafetyBrief>(briefs: readonly B[]): B[] {
  return live(briefs).sort(
    (a, b) =>
      b.brief_date.localeCompare(a.brief_date) ||
      b.opened_at.localeCompare(a.opened_at) ||
      a.id.localeCompare(b.id),
  );
}

/** The brief or meeting before this one (by date and time), for "Since the last brief". */
export function lastBriefBefore(
  briefs: readonly SafetyBrief[],
  at: { readonly brief_date: string; readonly opened_at: string; readonly id?: string },
): SafetyBrief | null {
  return (
    briefsNewestFirst(briefs).find(
      (b) =>
        b.id !== at.id &&
        (b.brief_date < at.brief_date || (b.brief_date === at.brief_date && b.opened_at < at.opened_at)),
    ) ?? null
  );
}

export function signsOf(signs: readonly SafetyBriefSign[], briefId: string): SafetyBriefSign[] {
  return live(signs)
    .filter((s) => s.brief_id === briefId)
    .sort((a, b) => a.signed_at.localeCompare(b.signed_at) || a.id.localeCompare(b.id));
}

/**
 * What the Pre-flight brief button does for this person today: start the day's brief, carry on with it (they led it
 * or have not signed and it is theirs to fill), re-confirm (someone else's brief they have not signed: their checks
 * and sign only, decision 974.1), or show it (they signed).
 */
export type BriefOpening = 'start' | 'continue' | 'reconfirm' | 'signed';

export function briefOpening(
  brief: SafetyBrief | null,
  signs: readonly SafetyBriefSign[],
  me: { readonly person_id: string | null; readonly user_id: string | null },
): BriefOpening {
  if (!brief) return 'start';
  const mine = signsOf(signs, brief.id).some(
    (s) =>
      (me.person_id !== null && s.person_id === me.person_id) ||
      (s.signed_by != null && s.signed_by === me.user_id),
  );
  if (mine) return 'signed';
  const leads =
    (me.person_id !== null && brief.leader_person_id === me.person_id) ||
    (brief.recorded_by != null && brief.recorded_by === me.user_id) ||
    // started on this phone and not in Teslyn Cloud yet
    (brief.recorded_by == null && me.person_id === brief.leader_person_id);
  return leads || !brief.finished_at ? 'continue' : 'reconfirm';
}

/** The crew of the day as attendees (decision 919: the crew dates decide), pilots first, then operators, then others. */
export function briefAttendeesOfDay(
  crew: readonly ProjectCrew[],
  people: readonly Pick<Person, 'id' | 'name'>[],
  date: string,
): BriefAttendee[] {
  const names = new Map(people.map((p) => [p.id, p.name]));
  const order = ['pilot', 'operator', 'manager', 'processor', 'office_staff'];
  const seen = new Set<string>();
  const out: BriefAttendee[] = [];
  for (const c of [...crewOnDate(crew, date)].sort((a, b) => order.indexOf(a.role) - order.indexOf(b.role))) {
    const name = (c.person_id ? names.get(c.person_id) : null) ?? c.person_name ?? '';
    const key = c.person_id ?? name;
    if (!name || seen.has(key)) continue;
    seen.add(key);
    out.push({ person_id: c.person_id, name, role: REGISTER_ROLE_LABEL[c.role], relief: false });
  }
  return out;
}

/** What is wrong with a brief before it is kept, in words. */
export function checkBrief(
  b: Pick<
    SafetyBrief,
    'kind' | 'brief_date' | 'location' | 'leader_name' | 'attendees' | 'items' | 'notes' | 'conditions'
  >,
): string[] {
  const out: string[] = [];
  if (!BRIEF_KINDS.includes(b.kind)) out.push('A brief is a pre-flight brief or a safety meeting.');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(b.brief_date)) out.push('The date is not a date.');
  if (b.location.length > BRIEF_LIMITS.location)
    out.push(`The place is at most ${BRIEF_LIMITS.location} characters.`);
  if (b.leader_name.trim() === '') out.push('Say who leads it.');
  if (b.attendees.length > BRIEF_LIMITS.attendees) out.push(`At most ${BRIEF_LIMITS.attendees} people.`);
  const seen = new Set<string>();
  for (const a of b.attendees) {
    if (a.name.trim() === '') out.push('Each person needs a name.');
    const k = a.person_id ?? a.name.trim().toLowerCase();
    if (seen.has(k)) out.push(`${a.name} is on the list twice.`);
    seen.add(k);
  }
  if (b.items.length > BRIEF_LIMITS.items) out.push(`At most ${BRIEF_LIMITS.items} items.`);
  if (b.items.some((i) => i.text.trim() === '')) out.push('An item has no words.');
  if (b.items.some((i) => i.text.length > BRIEF_LIMITS.item))
    out.push(`An item is at most ${BRIEF_LIMITS.item} characters.`);
  if (b.notes.length > BRIEF_LIMITS.notes)
    out.push(`The notes are at most ${BRIEF_LIMITS.notes} characters.`);
  if (b.conditions.note.length > BRIEF_LIMITS.conditionsNote)
    out.push(`The weather note is at most ${BRIEF_LIMITS.conditionsNote} characters.`);
  return [...new Set(out)];
}

export interface NewBriefInput {
  readonly id: string;
  readonly projectId: string;
  readonly kind: BriefKind;
  readonly date: string;
  readonly nowIso: string;
  readonly location: string;
  readonly leader: { readonly person_id: string | null; readonly name: string };
  readonly attendees: readonly BriefAttendee[];
  readonly checks: readonly CrewCheck[];
  readonly snapshot: BriefSnapshot;
  readonly hazards: readonly BriefHazard[];
}

/** A new brief or meeting. A meeting has no blocks, hazards, checks or conditions (decision 974.2). */
export function newBrief(i: NewBriefInput): SafetyBrief {
  const meeting = i.kind === 'safety_meeting';
  return {
    id: i.id,
    project_id: i.projectId,
    kind: i.kind,
    brief_date: i.date,
    opened_at: i.nowIso,
    location: i.location.trim().slice(0, BRIEF_LIMITS.location),
    leader_person_id: i.leader.person_id,
    leader_name: i.leader.name.trim(),
    attendees: i.attendees,
    snapshot: meeting
      ? { ...i.snapshot, aircraft: [], blocks: [], finished: [], far_hazards: '' }
      : i.snapshot,
    hazards: meeting ? [] : i.hazards,
    checks: meeting ? [] : i.checks,
    conditions: { reviewed: false, note: '' },
    items: [],
    notes: '',
    next_meeting: null,
    finished_at: null,
    content_changed_at: i.nowIso,
    pdf_name: '',
    pdf_sha256: '',
    pdf_revision: 0,
    pdf_made_at: null,
    recorded_by_name: i.leader.name.trim(),
    created_at: i.nowIso,
    updated_at: i.nowIso,
    deleted_at: null,
  };
}

/** The columns whose change makes earlier signs "signed before the last change". */
const CONTENT: readonly (keyof SafetyBrief)[] = [
  'kind',
  'brief_date',
  'location',
  'attendees',
  'hazards',
  'checks',
  'conditions',
  'items',
  'notes',
  'next_meeting',
];

/** The brief changed by the crew: `content_changed_at` moves when what people signed for changed. */
export function changedBrief(before: SafetyBrief, patch: Partial<SafetyBrief>, nowIso: string): SafetyBrief {
  const next = { ...before, ...patch, updated_at: nowIso };
  const content = CONTENT.some((k) => JSON.stringify(before[k]) !== JSON.stringify(next[k]));
  return content ? { ...next, content_changed_at: nowIso } : next;
}

/** Tick or untick one hazard briefed. */
export function withHazardBriefed(
  b: SafetyBrief,
  ref: string,
  briefed: boolean,
  nowIso: string,
): SafetyBrief {
  return changedBrief(b, { hazards: b.hazards.map((h) => (h.ref === ref ? { ...h, briefed } : h)) }, nowIso);
}

// ------------------------------------------------------------------------------------------------ signs

export type SignState = 'current' | 'before_change';

/** A sign made before the brief's content last changed says so (proposal §4.10). */
export function signState(
  s: Pick<SafetyBriefSign, 'signed_at'>,
  b: Pick<SafetyBrief, 'content_changed_at'>,
): SignState {
  return s.signed_at < b.content_changed_at ? 'before_change' : 'current';
}

/** "Signed 07:42 on their own phone", "Signed 07:44 on the leader's phone · re-confirm · signed before the last change". */
export function signWords(
  s: SafetyBriefSign,
  b: Pick<SafetyBrief, 'content_changed_at'>,
  time: (iso: string) => string,
): string {
  return [
    `Signed ${time(s.signed_at)} ${s.on_leader_phone ? 'on the leader’s phone' : 'on their own phone'}`,
    s.reconfirm ? 're-confirm' : '',
    signState(s, b) === 'before_change' ? 'signed before the last change' : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

/** The checks the person did not tick (shown in warn colour; never softened). */
export function uncheckedFor(
  s: Pick<SafetyBriefSign, 'checks' | 'role'>,
  b: Pick<SafetyBrief, 'checks'>,
): CrewCheck[] {
  const ticked = new Set(s.checks);
  return checksFor(b.checks, s.role).filter((c) => !ticked.has(c.id));
}

export interface NewSignInput {
  readonly id: string;
  readonly brief: Pick<SafetyBrief, 'id' | 'project_id' | 'checks'>;
  readonly person: { readonly person_id: string | null; readonly name: string; readonly role: string };
  readonly checks: readonly string[];
  readonly reconfirm: boolean;
  readonly onLeaderPhone: boolean;
  readonly nowIso: string;
}

/** A "Read and understood": only the person's own checks are kept. */
export function newSign(i: NewSignInput): SafetyBriefSign {
  const theirs = new Set(checksFor(i.brief.checks, i.person.role).map((c) => c.id));
  return {
    id: i.id,
    project_id: i.brief.project_id,
    brief_id: i.brief.id,
    person_id: i.person.person_id,
    name: i.person.name.trim(),
    role: i.person.role,
    checks: [...new Set(i.checks)].filter((c) => theirs.has(c)),
    reconfirm: i.reconfirm,
    on_leader_phone: i.onLeaderPhone,
    signed_at: i.nowIso,
    created_at: i.nowIso,
    updated_at: i.nowIso,
    deleted_at: null,
  };
}

/** The attendees who have not signed yet. */
export function notSignedYet(b: SafetyBrief, signs: readonly SafetyBriefSign[]): BriefAttendee[] {
  const s = signsOf(signs, b.id);
  return b.attendees.filter(
    (a) =>
      !s.some(
        (x) => (a.person_id !== null && x.person_id === a.person_id) || x.name.trim() === a.name.trim(),
      ),
  );
}

// ------------------------------------------------------------------------------------------------ actions

/** The open actions of the project (from any brief or meeting), due first, then oldest. */
export function openActions(actions: readonly SafetyAction[]): SafetyAction[] {
  return live(actions)
    .filter((a) => a.done_at === null)
    .sort(
      (a, b) =>
        (a.due_date ?? '9999').localeCompare(b.due_date ?? '9999') ||
        a.created_at.localeCompare(b.created_at) ||
        a.id.localeCompare(b.id),
    );
}

/** The actions ticked done since a time (ISO; all when null): SMS-02's "Actions finalised". */
export function actionsDoneSince(actions: readonly SafetyAction[], sinceIso: string | null): SafetyAction[] {
  return live(actions)
    .filter((a) => a.done_at !== null && (sinceIso === null || a.done_at > sinceIso))
    .sort((a, b) => (a.done_at ?? '').localeCompare(b.done_at ?? ''));
}

export interface ActionDraft {
  readonly text: string;
  readonly owner_person_id: string | null;
  readonly owner_name: string;
  readonly all_crew: boolean;
  readonly due_date: string;
}

/** What is wrong with an action, in words. "All crew" only when chosen on purpose (proposal §4.9). */
export function checkAction(d: ActionDraft): string[] {
  const out: string[] = [];
  const t = d.text.trim();
  if (t === '') out.push('Say what is to be done.');
  if (t.length > BRIEF_LIMITS.action) out.push(`An action is at most ${BRIEF_LIMITS.action} characters.`);
  if (!d.all_crew && d.owner_name.trim() === '') out.push('Choose who does it, or All crew.');
  if (d.due_date.trim() !== '' && !/^\d{4}-\d{2}-\d{2}$/.test(d.due_date.trim()))
    out.push('By when is a date (YYYY-MM-DD).');
  return out;
}

export function newAction(i: {
  readonly id: string;
  readonly brief: Pick<SafetyBrief, 'id' | 'project_id'>;
  readonly draft: ActionDraft;
  readonly nowIso: string;
}): SafetyAction {
  const d = i.draft;
  return {
    id: i.id,
    project_id: i.brief.project_id,
    brief_id: i.brief.id,
    text: d.text.trim(),
    owner_person_id: d.all_crew ? null : d.owner_person_id,
    owner_name: d.all_crew ? '' : d.owner_name.trim(),
    all_crew: d.all_crew,
    due_date: d.due_date.trim() || null,
    done_at: null,
    done_brief_id: null,
    done_by_name: '',
    created_at: i.nowIso,
    updated_at: i.nowIso,
    deleted_at: null,
  };
}

/** Tick an action done in a brief. */
export function actionDone(a: SafetyAction, briefId: string, byName: string, nowIso: string): SafetyAction {
  return { ...a, done_at: nowIso, done_brief_id: briefId, done_by_name: byName.trim(), updated_at: nowIso };
}

/** "Kyle Gardner by Fri 6 Nov", "All crew". */
export function actionOwnerWords(a: Pick<SafetyAction, 'all_crew' | 'owner_name' | 'due_date'>): string {
  const who = a.all_crew ? 'All crew' : a.owner_name || 'No one named';
  return a.due_date ? `${who} by ${jspPrintedDate(a.due_date)}` : who;
}

// ------------------------------------------------------------------------------------------------ since the last brief

/**
 * "Since the last brief" in words (proposal §4.4): the daily entries after the last brief's date up to today, the hazard
 * pins added since it, and the actions ticked done since. The first brief of a project looks back three days.
 */
export function sinceLastBrief(input: {
  readonly last: Pick<SafetyBrief, 'brief_date' | 'opened_at'> | null;
  readonly today: string;
  readonly entries: readonly DailyEntry[];
  readonly aircraft: readonly { readonly id: string; readonly registration: string }[];
  readonly pins: readonly HazardPinRecord[];
  readonly actions: readonly SafetyAction[];
}): string[] {
  const from = input.last?.brief_date ?? addDays(input.today, -3);
  const rego = new Map(input.aircraft.map((a) => [a.id, a.registration]));
  const out: string[] = [];
  const entries = input.entries
    .filter((e) => e.deleted_at === null && e.date >= from && e.date < input.today)
    .filter((e) => !(input.last && e.date === input.last.brief_date && e.created_at < input.last.opened_at))
    .sort(
      (a, b) => a.date.localeCompare(b.date) || (a.takeoff_time ?? '').localeCompare(b.takeoff_time ?? ''),
    );
  for (const e of entries) {
    const day = jspPrintedDate(e.date);
    const comment = e.op_comment.trim();
    if (e.entry_type === 'flight') {
      const parts = [
        `${day} flight`,
        e.aircraft_id ? (rego.get(e.aircraft_id) ?? '') : '',
        e.takeoff_time ? `take-off ${e.takeoff_time.slice(0, 5)}` : '',
        e.svy_hours > 0 ? `survey ${e.svy_hours.toFixed(1)} h` : '',
      ].filter(Boolean);
      out.push(`${parts.join(', ')}${comment ? `: ${comment}` : ''}`);
    } else {
      const what =
        e.entry_type === 'standby'
          ? `standby${e.standby_type ? ` (${e.standby_type.replace('_', ' ')})` : ''}`
          : e.entry_type;
      out.push(`${day} no flying, ${what}${comment ? `: ${comment}` : ''}`);
    }
  }
  const since = input.last?.opened_at ?? `${from}T00:00:00Z`;
  for (const p of live(input.pins).filter((x) => x.created_at > since))
    out.push(`Hazard pin added: ${p.name || 'unnamed'}${p.placed_by_name ? ` (${p.placed_by_name})` : ''}`);
  for (const a of actionsDoneSince(input.actions, input.last?.opened_at ?? null))
    out.push(`Action done: ${a.text}${a.done_by_name ? ` (${a.done_by_name})` : ''}`);
  return out;
}

function addDays(date: string, n: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// ------------------------------------------------------------------------------------------------ the PDF

export const BRIEF_MANIFEST_SCHEMA = 'teslyn.safety-brief.v1';

/**
 * `<project folder>_Preflight_Brief_<d-Month-yyyy>.pdf` or `_Safety_Meeting_<d-Month-yyyy>.pdf`; `n` (from 2) for a
 * second record of the same kind and day. Never a revision number (R139); the database keeps `pdf_revision`.
 */
export function briefPdfFileName(
  folderName: string,
  b: Pick<SafetyBrief, 'kind' | 'brief_date'>,
  n = 1,
): string {
  const base = folderName.replace(/[\\/:*?"<>|]+/g, '_').trim() || 'Project';
  const kind = b.kind === 'preflight' ? 'Preflight_Brief' : 'Safety_Meeting';
  return `${base}_${kind}_${jspFileDate(b.brief_date)}${n > 1 ? `_${n}` : ''}.pdf`;
}

/** Which of the same kind and day this record is (1, 2, ...), by the time it was started. */
export function briefSameDayNumber(briefs: readonly SafetyBrief[], b: SafetyBrief): number {
  const same = live(briefs)
    .filter((x) => x.kind === b.kind && x.brief_date === b.brief_date)
    .sort((x, y) => x.opened_at.localeCompare(y.opened_at) || x.id.localeCompare(y.id));
  return Math.max(1, same.findIndex((x) => x.id === b.id) + 1);
}

/**
 * Whether a laptop should make the PDF (again): the record is finished and has no PDF, or something it prints changed
 * after the PDF (its content, a sign, an action of it).
 */
export function briefNeedsNewPdf(
  b: Pick<SafetyBrief, 'finished_at' | 'pdf_made_at' | 'content_changed_at' | 'updated_at' | 'deleted_at'>,
  signs: readonly Pick<SafetyBriefSign, 'updated_at'>[],
  actions: readonly Pick<SafetyAction, 'updated_at'>[] = [],
): boolean {
  if (b.deleted_at !== null || b.finished_at === null) return false;
  if (!b.pdf_made_at) return true;
  const made = b.pdf_made_at;
  return (
    b.content_changed_at > made ||
    b.finished_at > made ||
    signs.some((s) => s.updated_at > made) ||
    actions.some((a) => a.updated_at > made)
  );
}

export interface BriefManifestTenant {
  readonly key: string;
  readonly name: string;
  readonly brand: string;
}

/** One block part of the PDF. */
export interface BriefManifestBlock {
  readonly title: string;
  readonly facts: readonly (readonly [string, string])[];
  /** The map as SVG text (`briefBlockSvg`), or '' when the block has no boundary or lines to draw. */
  readonly map_svg: string;
  readonly hazards: readonly {
    readonly n: string;
    readonly name: string;
    readonly what: string;
    readonly where: string;
    readonly briefed: string;
  }[];
}

/** The manifest `report::safety_brief` renders (mirrored with serde in crates/teslyn-core). */
export interface BriefManifest {
  readonly schema: typeof BRIEF_MANIFEST_SCHEMA;
  readonly tenant: BriefManifestTenant;
  readonly title: string;
  readonly job_number: string;
  readonly header: readonly (readonly [string, string])[];
  readonly aircraft: readonly string[];
  readonly attendees: readonly { readonly name: string; readonly role: string }[];
  readonly blocks: readonly BriefManifestBlock[];
  readonly finished: readonly string[];
  readonly far_hazards: string;
  readonly since_last: readonly string[];
  readonly standing: readonly string[];
  readonly conditions: string;
  /** The NAIPS weather and NOTAMs the crew saw (decision 989), as NAIPS printed them; [] when none. */
  readonly weather: readonly BriefWeatherBlock[];
  readonly checks: readonly { readonly text: string; readonly who: string }[];
  readonly items: readonly { readonly kind: string; readonly text: string }[];
  readonly notes: string;
  readonly actions_open: readonly { readonly text: string; readonly who: string; readonly raised: string }[];
  readonly actions_done: readonly { readonly text: string; readonly who: string; readonly done: string }[];
  readonly signs: readonly {
    readonly name: string;
    readonly role: string;
    readonly how: string;
    readonly when: string;
    readonly unchecked: string;
  }[];
  readonly not_signed: readonly string[];
  readonly footer_text: string;
}

/** "26/10/2026 07:42" in the laptop's time zone, given by the caller (`time`). */
export function briefManifest(input: {
  readonly tenant: BriefManifestTenant;
  readonly project: { readonly number: string; readonly title: string; readonly client_name: string };
  readonly brief: SafetyBrief;
  readonly signs: readonly SafetyBriefSign[];
  readonly actions: readonly SafetyAction[];
  /** Every brief of the project, for the actions raised elsewhere. */
  readonly briefs: readonly SafetyBrief[];
  /** The block maps by block id (SVG). */
  readonly maps: Readonly<Record<string, string>>;
  readonly time: (iso: string) => string;
}): BriefManifest {
  const { brief: b, project: p, time } = input;
  const preflight = b.kind === 'preflight';
  const signs = signsOf(input.signs, b.id);
  const raisedIn = new Map(
    input.briefs.map((x) => [x.id, `${BRIEF_KIND_WORDS[x.kind]} ${jspPrintedDate(x.brief_date)}`]),
  );
  const done = live(input.actions).filter((a) => a.done_brief_id === b.id);
  const open = openActions(input.actions).filter(
    (a) => a.created_at <= (b.finished_at ?? '9999') || a.brief_id === b.id,
  );
  return {
    schema: BRIEF_MANIFEST_SCHEMA,
    tenant: input.tenant,
    title: BRIEF_KIND_WORDS[b.kind],
    job_number: p.number,
    header: [
      ['Project', [p.number, p.title].filter((x) => x.trim()).join(' ')],
      ['Client', p.client_name],
      ['Date', jspPrintedDate(b.brief_date)],
      ['Opened', time(b.opened_at)],
      ['Place', b.location],
      ['Led by', b.leader_name],
      ...(b.snapshot.as_at
        ? ([['Lines and hazards as at', time(b.snapshot.as_at)]] as [string, string][])
        : []),
      ...(b.next_meeting ? ([['Next meeting', jspPrintedDate(b.next_meeting)]] as [string, string][]) : []),
    ].filter((row) => (row[1] ?? '').trim() !== '') as [string, string][],
    aircraft: b.snapshot.aircraft.map((a) => [a.registration, ...a.notes].join(' · ')),
    attendees: b.attendees.map((a) => ({ name: a.name, role: a.relief ? `${a.role} (relief)` : a.role })),
    blocks: preflight
      ? b.snapshot.blocks.map((s) => ({
          title: s.name,
          facts: [
            ['Lines', s.text],
            ...(s.counts.length ? ([['States', s.counts.join(', ')]] as [string, string][]) : []),
            ...(s.open.length ? ([['Reflights and patches', s.open.join('; ')]] as [string, string][]) : []),
            ...s.facts.map((f) => ['', f] as [string, string]),
          ],
          map_svg: input.maps[s.block_id] ?? '',
          hazards: b.hazards
            .filter((h) => h.blocks.includes(s.name))
            .map((h) => ({
              n: h.n,
              name: h.name,
              what: h.detail,
              where: hazardWhereWords(h, s.name),
              briefed: h.briefed ? 'Briefed' : 'Not briefed',
            })),
        }))
      : [],
    finished: preflight ? b.snapshot.finished : [],
    far_hazards: preflight ? b.snapshot.far_hazards : '',
    since_last: b.snapshot.since_last,
    standing: b.snapshot.standing,
    conditions: preflight
      ? `Weather and NOTAMs: ${b.conditions.reviewed ? 'reviewed by the crew' : 'not reviewed'}${b.conditions.note.trim() ? `. ${b.conditions.note.trim()}` : ''}`
      : '',
    weather: preflight ? briefWeatherBlocks(b.snapshot.weather, b.opened_at) : [],
    checks: b.checks.map((c) => ({ text: c.text, who: CHECK_WHO_WORDS[c.who] })),
    items: b.items.map((i) => ({ kind: BRIEF_ITEM_WORDS[i.kind], text: i.text })),
    notes: b.notes.trim(),
    actions_open: open.map((a) => ({
      text: a.text,
      who: actionOwnerWords(a),
      raised: raisedIn.get(a.brief_id) ?? '',
    })),
    actions_done: done.map((a) => ({
      text: a.text,
      who: actionOwnerWords(a),
      done: `${a.done_by_name ? `${a.done_by_name}, ` : ''}${a.done_at ? time(a.done_at) : ''}`,
    })),
    signs: signs.map((s) => ({
      name: s.name,
      role: s.role,
      how: [
        s.on_leader_phone ? 'Signed on the leader’s phone' : 'Own phone',
        s.reconfirm ? 're-confirm' : '',
        signState(s, b) === 'before_change' ? 'signed before the last change' : '',
      ]
        .filter(Boolean)
        .join(' · '),
      when: time(s.signed_at),
      unchecked: uncheckedFor(s, b)
        .map((c) => c.text)
        .join('; '),
    })),
    not_signed: notSignedYet(b, input.signs).map((a) => a.name),
    footer_text: `${input.tenant.name} ${BRIEF_KIND_WORDS[b.kind].toLowerCase()} ${p.number} ${jspPrintedDate(b.brief_date)}`,
  };
}

/** The client's view (decision 974.6): the PDFs of a project's briefs when the project's setting is on. */
export function briefsVisibleToClient(
  setting: Pick<BriefSetting, 'client_visible' | 'deleted_at'> | null,
): boolean {
  return !!setting && setting.deleted_at === null && setting.client_visible;
}
